import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import templateApi from '../../api/templateApi';
import { resolveAssetUrl } from '../../api/adminAlertTemplate.api';
import './alert-templates.css';

const TABS = [
  { id: 'all', label: 'ALL' },
  { id: 'templates', label: 'TEMPLATES' },
  { id: 'ready', label: 'READY TO SEND' },
  { id: 'active', label: 'ACTIVE' },
  { id: 'inactive', label: 'INACTIVE' },
];

const TEMPLATE_TYPE_LABELS = {
  alert: 'Alert',
  additional: 'Additional',
  manual_entry: 'Manual Entry',
  none: 'None of this',
};

function formatTemplateType(type) {
  return TEMPLATE_TYPE_LABELS[type] || type || 'Alert';
}

function TemplateCard({ item, onEdit, onClone, onDelete, onToggleActive }) {
  const preview = resolveAssetUrl(item.previewurl);
  const templateType = item.type || 'alert';
  const isActive = item.isActive ?? item.is_active ?? true;

  return (
    <div className="at-card">
      <div className="at-card-preview">
        {preview ? (
          <img src={preview} alt={item.name} />
        ) : (
          <div className="at-card-placeholder">No preview</div>
        )}
      </div>
      <div className="at-card-body">
        <h3>{item.name}</h3>
        <div className="at-card-badges">
          <span className={`at-badge at-badge-${item.status}`}>{item.status}</span>
          <span className={`at-badge ${isActive ? 'at-badge-active' : 'at-badge-inactive'}`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
          <span className={`at-badge at-badge-type at-badge-type-${templateType}`}>
            {formatTemplateType(templateType)}
          </span>
        </div>
        <div className="at-card-actions">
          <button type="button" className="at-btn at-btn-sm" onClick={() => onEdit(item.id)}>Edit</button>
          <button
            type="button"
            className={`at-btn at-btn-sm ${isActive ? 'at-btn-warning' : 'at-btn-success'}`}
            onClick={() => onToggleActive(item)}
            title={isActive ? 'Deactivate template' : 'Activate template'}
          >
            {isActive ? 'Deactivate' : 'Activate'}
          </button>
          <button type="button" className="at-btn at-btn-sm at-btn-secondary" onClick={() => onClone(item)}>Clone</button>
          <button type="button" className="at-btn at-btn-sm at-btn-danger" onClick={() => onDelete(item.id)}>Delete</button>
        </div>
      </div>
    </div>
  );
}

export default function AlertTemplateList() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmModal, setConfirmModal] = useState(null); // { item, targetStatus }

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const all = await templateApi.listAll();
      setTemplates(all);
    } catch (err) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const displayed = useMemo(() => {
    if (activeTab === 'templates') {
      return templates.filter((t) => t.status === 'template');
    }
    if (activeTab === 'ready') {
      return templates.filter((t) => t.status === 'ready');
    }
    if (activeTab === 'active') {
      return templates.filter((t) => (t.isActive ?? t.is_active ?? true) === true);
    }
    if (activeTab === 'inactive') {
      return templates.filter((t) => (t.isActive ?? t.is_active ?? true) === false);
    }
    return templates;
  }, [activeTab, templates]);

  const handleCreate = async () => {
    try {
      const created = await templateApi.createNew('New Alert Template');
      navigate(`/alert-templates/${created.id}/edit`);
    } catch (err) {
      setError(err.message || 'Failed to create template');
    }
  };

  const handleEdit = (id) => navigate(`/alert-templates/${id}/edit`);

  const handleClone = async (item) => {
    try {
      const cloned = await templateApi.clone(item);
      navigate(`/alert-templates/${cloned.id}/edit`);
    } catch (err) {
      setError(err.message || 'Clone failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await templateApi.deleteTemplate(id);
      load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const handleToggleActiveClick = (item) => {
    const currentActive = item.isActive ?? item.is_active ?? true;
    setConfirmModal({
      item,
      targetStatus: !currentActive,
    });
  };

  const confirmToggleActive = async () => {
    if (!confirmModal) return;
    const { item, targetStatus } = confirmModal;
    setConfirmModal(null);
    try {
      await templateApi.setActiveStatus(item.id, targetStatus);
      // Update local state optimistically or reload
      setTemplates((prev) =>
        prev.map((t) => (t.id === item.id ? { ...t, isActive: targetStatus, is_active: targetStatus } : t))
      );
    } catch (err) {
      setError(err.message || 'Failed to update template active status');
    }
  };

  return (
    <div className="at-page">
      <PageHeader
        title="Message Templates"
        icon="fa-palette"
        actions={(
          <button type="button" className="at-btn at-btn-primary" onClick={handleCreate}>
            + Create New
          </button>
        )}
      />

      {error && <div className="at-error">{error}</div>}

      {/* Status Confirmation Modal */}
      {confirmModal && (
        <div className="at-modal-overlay">
          <div className="at-modal">
            <h3>Confirm Status Change</h3>
            <p>
              Are you sure you want to mark <strong>{confirmModal.item.name}</strong> as{' '}
              <strong style={{ color: confirmModal.targetStatus ? '#16A34A' : '#DC2626' }}>
                {confirmModal.targetStatus ? 'ACTIVE' : 'INACTIVE'}
              </strong>
              ?
            </p>
            {!confirmModal.targetStatus && (
              <p className="at-hint-text">
                When marked as <strong>Inactive</strong>, this template will be excluded from the template list in the web application and cannot be used by users.
              </p>
            )}
            <div className="at-modal-actions">
              <button
                type="button"
                className="at-btn at-btn-secondary"
                onClick={() => setConfirmModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`at-btn ${confirmModal.targetStatus ? 'at-btn-success' : 'at-btn-danger'}`}
                onClick={confirmToggleActive}
              >
                Confirm {confirmModal.targetStatus ? 'Activate' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="at-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`at-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p>Loading templates…</p>
      ) : (
        <div className="at-grid">
          <div className="at-create-card" onClick={handleCreate} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleCreate()}>
            <span className="at-create-icon">+</span>
            <p>Create New Alert Template</p>
          </div>
          {displayed.map((item) => (
            <TemplateCard
              key={item.id}
              item={item}
              onEdit={handleEdit}
              onClone={handleClone}
              onDelete={handleDelete}
              onToggleActive={handleToggleActiveClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

